import React, { useEffect, useMemo, useState } from 'react'
import Button from '@material-ui/core/Button'
import TextField from '@material-ui/core/TextField'
import Radio from '@material-ui/core/Radio'
import FormControlLabel from '@material-ui/core/FormControlLabel'
import CircularProgress from '@material-ui/core/CircularProgress'
import ExpandMoreIcon from '@material-ui/icons/ExpandMore'
import SlidePanel from 'Components/SlidePanel/SlidePanel'
import useFleetJobChannels from 'Hooks/useFleetJobChannels'

const matchesSearch = (channel, term) => !term || [channel.nameLocalized, channel.code, channel.unit]
  .some((text) => String(text || '').toLowerCase().includes(term))

const isChannelSelected = (selectedChannelId, channel) => selectedChannelId != null && channel.id === selectedChannelId

const channelLabel = (channel) => channel.nameLocalized || channel.code || String(channel.id)

const isUnitSelectable = (jobUnit) => jobUnit.isEnabled !== false && jobUnit.isInSubscription !== false

/** Unit section with its channels — same look as the Manage Channels panel, single choice. */
const ChannelGroup = ({ jobUnit, channels, totalCount, defaultOpen, forceOpen, selected, onSelect, notInSubscriptionLabel }) => {
  const [open, setOpen] = useState(defaultOpen)
  const isExpanded = open || forceOpen
  const enabled = isUnitSelectable(jobUnit)
  const name = jobUnit.nameLocalized || jobUnit.name
  const selectedCount = (jobUnit.channels || []).some((channel) => isChannelSelected(selected, channel)) ? 1 : 0

  return (
    <div className={`channelGroup${!enabled ? ' channelGroup--disabled' : ''}`}>
      <div className="channelGroupHead" onClick={() => setOpen((prev) => !prev)}>
        <ExpandMoreIcon className={`channelGroupChevron${isExpanded ? ' channelGroupChevron--open' : ''}`} />
        <span
          className="channelGroupName"
          title={jobUnit.isInSubscription === false ? `${name} · ${notInSubscriptionLabel}` : name}
        >
          {name}
        </span>
        <span className="channelTableCount">{selectedCount} / {totalCount}</span>
      </div>
      {isExpanded ? (
        <div className="channelGroupBody">
          {channels.map((channel) => {
            const label = channelLabel(channel)
            const checked = isChannelSelected(selected, channel)
            const unit = channel.unit && channel.unit !== '-' ? channel.unit : ''
            return (
              <div
                className={`channelRow${checked ? ' channelRow--selected' : ''}${!enabled ? ' channelRow--disabled' : ''}`}
                key={channel.id}
              >
                <FormControlLabel
                  className="channelRowLabel"
                  control={(
                    <Radio
                      color="default"
                      size="small"
                      checked={checked}
                      disabled={!enabled}
                      onChange={() => onSelect?.({ channelId: channel.id, label })}
                    />
                  )}
                  label={label}
                  title={channel.code || label}
                />
                <span className="channelRowUnit channelRowUnit--static">{unit}</span>
              </div>
            )
          })}
        </div>
      ) : null}
    </div>
  )
}

/**
 * Pressure channels of every unit in the blender (Job 0) and MPC (5) jobs, laid out like Manage Channels.
 * onApply(channelId, label)
 * Picking a channel overrides the source channel of one tile field (session only); Apply commits the draft.
 */
const FleetChannelsSlidePanel = ({
  open,
  onClose,
  jobIds,
  unitUId,
  unitName,
  title,
  selectedChannelId,
  canReset = false,
  onApply,
  onReset,
  translation,
}) => {
  const [search, setSearch] = useState('')
  const [draft, setDraft] = useState(null)
  const { units, isLoading, isError } = useFleetJobChannels(jobIds, unitUId, open, unitName)

  useEffect(() => {
    if (!open) return
    setDraft(null)
    setSearch('')
  }, [open])

  const term = search.trim().toLowerCase()

  const groups = useMemo(() => units
    .map((u) => {
      const selectable = (u.channels || []).filter((channel) => channel.id != null)
      return {
        jobUnit: u,
        totalCount: selectable.length,
        channels: selectable.filter((channel) => matchesSearch(channel, term)),
      }
    })
    .filter((group) => group.channels.length), [units, term])

  const visibleCount = groups.reduce((sum, group) => sum + group.channels.length, 0)
  const totalCount = useMemo(() => units
    .filter(isUnitSelectable)
    .reduce((sum, u) => sum + (u.channels || []).filter((channel) => channel.id != null).length, 0), [units])

  const current = draft ? draft.channelId : selectedChannelId
  const selectedCount = units.some((u) => (u.channels || []).some((channel) => isChannelSelected(current, channel))) ? 1 : 0
  const isChanged = Boolean(draft) && draft.channelId !== selectedChannelId

  const handleApply = () => {
    if (isChanged) onApply?.(draft.channelId, draft.label)
    onClose?.()
  }

  const handleReset = () => {
    onReset?.()
    onClose?.()
  }

  let status = null
  if (!jobIds?.length) status = translation?.fleetChannelPickerNoJobLabel || 'No active job for this unit'
  else if (isLoading) status = <CircularProgress size={28} />
  else if (isError) status = translation?.fleetChannelPickerErrorLabel || 'Failed to load channels'
  else if (!visibleCount) status = translation?.fleetChannelPickerEmptyLabel || 'No channels'

  return (
    <SlidePanel
      open={open}
      onClose={onClose}
      title={title || translation?.fleetChannelsPanelTitle || 'Channels'}
      className="fdChannelsSlidePanel"
      footer={(
        <>
          {canReset ? (
            <Button className="fdChannelsResetBtn" onClick={handleReset}>
              {translation?.fleetResetChannelLabel || 'Reset to default'}
            </Button>
          ) : null}
          <Button variant="outlined" onClick={onClose}>
            {translation?.fleetChannelsCancelLabel || 'Cancel'}
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleApply}
            disabled={!isChanged}
          >
            {translation?.fleetChannelsApplyLabel || 'Apply'}
          </Button>
        </>
      )}
    >
      <TextField
        variant="outlined"
        margin="normal"
        fullWidth
        name="search"
        label={translation?.dashboardSearchChannelLabel || translation?.fleetChannelPickerSearchLabel || 'Search channel'}
        value={search}
        onChange={(e) => setSearch(e.target.value)}
      />
      <div className="channelTable">
        <div className="channelTableHead">
          <span className="channelTableHeadTitle">{translation?.dashboardChannelsLabel || 'Channels'}</span>
          <span className="channelTableCount">{selectedCount} / {totalCount}</span>
        </div>
        <div className="channelTableBody">
          {status ? (
            <div className="fdChannelsStatus">{status}</div>
          ) : groups.map((group) => (
            <ChannelGroup
              key={group.jobUnit.id ?? group.jobUnit.uId}
              jobUnit={group.jobUnit}
              channels={group.channels}
              totalCount={group.totalCount}
              defaultOpen={units.length === 1}
              forceOpen={Boolean(term)}
              selected={current}
              onSelect={setDraft}
              notInSubscriptionLabel={translation?.fleetUnitNotBillableLabel || 'Not in subscription'}
            />
          ))}
        </div>
      </div>
    </SlidePanel>
  )
}

export default FleetChannelsSlidePanel
